import { useCallback } from 'react'
import { usePublishScheduledPost } from 'hooks/queries/scheduledPosts/usePublishScheduledPost'
import { useUpdateScheduledPost } from 'hooks/queries/scheduledPosts/useUpdateScheduledPost'
import { EmailPostSettings } from 'api/clients/ScheduledPostsClient'
import type { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'

export interface EmailPublishingParams {
  listId: string
  fromName: string
  replyTo: string
  subject: string
}

export const useEmailPublishing = (scheduledPost: ScheduledPostShow | undefined) => {
  const publishScheduledPost = usePublishScheduledPost()
  const updateScheduledPost = useUpdateScheduledPost({ suppressToast: true })

  const handleEmailPublish = useCallback(
    async (params: EmailPublishingParams) => {
      if (!scheduledPost) return

      try {
        // Step 1: Update scheduled post settings with user selections from frontend
        // The backend needs these to know which list to send to, subject line, etc.
        const currentSettings = (scheduledPost.post_settings as EmailPostSettings) || {}
        await updateScheduledPost.mutateAsync({
          id: scheduledPost.id,
          params: {
            postSettings: {
              ...currentSettings,
              list_id: params.listId,
              subject_line: params.subject,
              from_name: params.fromName,
              reply_to: params.replyTo,
            },
          },
        })

        // Step 2: Determine if this is scheduled or immediate based on time parameter

        // Step 3: Update post_at time (same pattern as WordPress/social media)

        await publishScheduledPost.mutateAsync({ id: scheduledPost.id })

        // Step 4: Use standard publish endpoint (same pattern as WordPress/social media)
        // Backend will read the post_settings we just updated

        // Note: Success/failure toasts and status updates are handled automatically
        // by the usePublishScheduledPost hook's polling mechanism
      } catch (error) {
        // Error handling is also managed by usePublishScheduledPost hook
        // console.error('Failed to publish email campaign:', error)
      }
    },
    [scheduledPost, updateScheduledPost, publishScheduledPost],
  )

  return {
    handleEmailPublish,
    isPublishing: publishScheduledPost.isLoading,
  }
}
